import type { StencilRotation } from '../../model/recipe';
import { drawGlyph, drawIcon, type IconId } from './bitmaps';
import {
  cleanupMask,
  countMaskCells,
  DEFAULT_CLEANUP,
  fitMaskIntoGrid,
  maskHash,
  mirrorMask,
  rescaleMask,
  rotateMask,
  trimMask,
  type CleanupOptions,
  type Mask,
} from './mask';
import type { StencilManifestEntry } from './manifest';
import { drawProceduralShape, type ProceduralShapeId } from './procedural';

export interface StencilProcessOptions {
  readonly gridWidth: number;
  readonly gridHeight: number;
  readonly rotation: StencilRotation;
  readonly mirrorHorizontal: boolean;
  readonly mirrorVertical: boolean;
  readonly coverageThresholdPermille?: number;
  readonly cleanup?: CleanupOptions;
  readonly minimumActiveCells?: number;
}

export type StencilProcessResult =
  | {
      readonly ok: true;
      readonly mask: Mask;
      readonly activeCells: number;
      readonly usableRatio: number;
      readonly sourceHash: string;
      readonly resultHash: string;
    }
  | { readonly ok: false; readonly reason: string; readonly detail: string };

/** Coverage threshold for the area-based downsample: a cell activates at 50% coverage. */
export const DEFAULT_COVERAGE_THRESHOLD_PERMILLE = 500;

export function loadStencilSource(entry: StencilManifestEntry): Mask {
  const [kind, name] = splitSource(entry.source);
  switch (kind) {
    case 'procedural':
      return drawProceduralShape(name as ProceduralShapeId);
    case 'icon':
      return drawIcon(name as IconId);
    case 'glyph':
      return drawGlyph(name);
    default:
      throw new Error(`Unsupported stencil source kind: ${kind}`);
  }
}

function splitSource(source: string): [string, string] {
  const separator = source.indexOf(':');
  if (separator < 0) throw new Error(`Malformed stencil source: ${source}`);
  return [source.slice(0, separator), source.slice(separator + 1)];
}

/**
 * The full deterministic stencil pipeline:
 * load -> permitted transform -> aspect-preserving fit -> centre -> cleanup -> validate.
 *
 * Failure is always reported rather than thrown so the director can deterministically fall
 * back to another stencil; one malformed entry can never break generation permanently.
 */
export function processStencil(
  entry: StencilManifestEntry,
  options: StencilProcessOptions,
): StencilProcessResult {
  const { gridWidth, gridHeight } = options;
  if (gridWidth <= 0 || gridHeight <= 0) {
    return { ok: false, reason: 'invalid-grid', detail: `${gridWidth}x${gridHeight}` };
  }

  let source: Mask;
  try {
    source = loadStencilSource(entry);
  } catch (error) {
    return { ok: false, reason: 'source-load-failed', detail: String(error) };
  }
  if (countMaskCells(source) === 0) {
    return { ok: false, reason: 'empty-source', detail: entry.id };
  }
  const sourceHash = maskHash(source);

  const rotation = entry.allowedRotations.includes(options.rotation) ? options.rotation : 0;
  const mirrorHorizontal = entry.allowHorizontalMirror && options.mirrorHorizontal;
  const mirrorVertical = entry.allowVerticalMirror && options.mirrorVertical;

  const transformed = mirrorMask(rotateMask(source, rotation), mirrorHorizontal, mirrorVertical);
  const threshold = options.coverageThresholdPermille ?? DEFAULT_COVERAGE_THRESHOLD_PERMILLE;

  /*
   * A stencil that declares a preferred aspect ratio is fitted inside the grid, keeping its
   * proportions — a stretched heart or house stops reading as one. Everything else is scaled
   * to the whole grid, which is what a geometric silhouette wants: fitting a square-drawn
   * rectangle into a tall grid would leave a third of the board unusable, and a board the
   * player cannot use is a board with no arrows on it.
   */
  const fitted =
    entry.preferredAspectRatio === undefined
      ? rescaleMask(trimMask(transformed), gridWidth, gridHeight, threshold)
      : fitMaskIntoGrid(transformed, gridWidth, gridHeight, threshold);

  const beforeCleanup = countMaskCells(fitted);
  const cleaned = cleanupMask(fitted, options.cleanup ?? DEFAULT_CLEANUP);
  const activeCells = countMaskCells(cleaned);

  if (activeCells === 0) {
    return { ok: false, reason: 'empty-after-cleanup', detail: entry.id };
  }
  // Cleanup that eats a large share of the silhouette means the stencil is not
  // recognisable at this grid size.
  if (beforeCleanup > 0 && activeCells * 4 < beforeCleanup * 3) {
    return {
      ok: false,
      reason: 'unrecognisable-after-cleanup',
      detail: `${activeCells}/${beforeCleanup}`,
    };
  }

  const usableRatio = activeCells / (gridWidth * gridHeight);
  if (usableRatio < entry.minimumUsableRatio) {
    return {
      ok: false,
      reason: 'below-usable-ratio',
      detail: `${usableRatio.toFixed(3)} < ${entry.minimumUsableRatio}`,
    };
  }
  const minimumActiveCells = options.minimumActiveCells ?? 12;
  if (activeCells < minimumActiveCells) {
    return { ok: false, reason: 'too-few-cells', detail: `${activeCells}` };
  }

  return {
    ok: true,
    mask: cleaned,
    activeCells,
    usableRatio,
    sourceHash,
    resultHash: maskHash(cleaned),
  };
}
